import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { normLang } from "@/i18n";
import api, { apiError, money } from "@/lib/api";
import { toast } from "sonner";
import { UserAvatar } from "@/components/shared/UserAvatar";
import {
  Users, Network, Scale, Gift, Home, ChevronRight, Plus, Minus, X, Crown,
  ArrowDownLeft, ArrowDownRight, Info, Layers,
} from "lucide-react";

const TXT = {
  fr: {
    newAccount: "Nouveau compte", ordersInProgress: "Commandes en cours", ordered: "A commandé", deposited: "A déposé",
    active: "Actif", you: "Vous",
    memberDetails: "Détails du membre", info: "Informations",
    directSponsor: "Parrain direct", placement: "Placement", rootYou: "Racine (Vous)", leftW: "Gauche", rightW: "Droite",
    generation: "Génération", volumes: "Volumes",
    leftVolume: "Volume gauche", rightVolume: "Volume droite", leftCarry: "Report gauche", rightCarry: "Report droite", matching: "Correspondance",
    binaryBonus: "Bonus Binaire", available: "Disponible", pending: "En attente", totalGenerated: "Total généré",
    network: "Réseau", descendants: "Descendants", membersWord: "membres", leftMembers: "Membres à gauche", rightMembers: "Membres à droite",
    viewNetwork: "Voir son réseau",
    pageTitle: "Mon Arbre Binaire", pageSub: "Visualisez et développez votre réseau. Cliquez sur un membre pour voir son équipe.",
    myLeftBranch: "Ma branche gauche", myRightBranch: "Ma branche droite", carry: "Report",
    currentMatching: "Correspondance actuelle", myDescendants: "Mes descendants", totalMembers: "Membres au total",
    myTree: "Mon arbre", backToTree: "Revenir à mon arbre", levels: "Niveaux",
    loadingNetwork: "Chargement de votre réseau…",
    clickMember: "Cliquez sur un membre pour afficher ses détails et explorer son réseau.",
    uplineNote: "Vous ne pouvez pas voir les membres au-dessus de votre position, sauf votre parrain direct.",
    statusLegend: "Légende des statuts",
    legNewDesc: "Compte créé, aucun dépôt", legDepDesc: "Dépôt effectué, aucune commande",
    legOrdDesc: "Commande(s) en cours", legActiveDesc: "Commandes réalisées",
    leftBranch: "Branche gauche", rightBranch: "Branche droite",
    howItWorks: "Comment ça marche ?",
    hiw1: "1. Cliquez sur un membre pour voir son équipe (toute sa lignée en dessous).",
    hiw2: "2. Vous pouvez voir toute la chaîne en dessous de vous.",
    hiw3: "3. Vous voyez uniquement votre parrain direct (pas les membres au-dessus).",
    hiw4: "4. Les informations affichées sont réelles et mises à jour en temps réel.",
    pts: (n) => `${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} ${Math.abs(Number(n || 0)) < 2 ? "pt" : "pts"}`,
  },
  en: {
    newAccount: "New account", ordersInProgress: "Orders in progress", ordered: "Ordered", deposited: "Deposited",
    active: "Active", you: "You",
    memberDetails: "Member details", info: "Information",
    directSponsor: "Direct sponsor", placement: "Placement", rootYou: "Root (You)", leftW: "Left", rightW: "Right",
    generation: "Generation", volumes: "Volumes",
    leftVolume: "Left volume", rightVolume: "Right volume", leftCarry: "Left carry", rightCarry: "Right carry", matching: "Matching",
    binaryBonus: "Binary Bonus", available: "Available", pending: "Pending", totalGenerated: "Total generated",
    network: "Network", descendants: "Descendants", membersWord: "members", leftMembers: "Members on the left", rightMembers: "Members on the right",
    viewNetwork: "View their network",
    pageTitle: "My Binary Tree", pageSub: "Visualize and expand your network. Click a member to see their team.",
    myLeftBranch: "My left branch", myRightBranch: "My right branch", carry: "Carry",
    currentMatching: "Current matching", myDescendants: "My descendants", totalMembers: "Total members",
    myTree: "My tree", backToTree: "Back to my tree", levels: "Levels",
    loadingNetwork: "Loading your network…",
    clickMember: "Click a member to view their details and explore their network.",
    uplineNote: "You cannot see members above your position, except your direct sponsor.",
    statusLegend: "Status legend",
    legNewDesc: "Account created, no deposit", legDepDesc: "Deposit made, no order",
    legOrdDesc: "Order(s) in progress", legActiveDesc: "Orders completed",
    leftBranch: "Left branch", rightBranch: "Right branch",
    howItWorks: "How does it work?",
    hiw1: "1. Click a member to see their team (their whole downline).",
    hiw2: "2. You can see the whole chain below you.",
    hiw3: "3. You only see your direct sponsor (not members above).",
    hiw4: "4. The information shown is real and updated in real time.",
    pts: (n) => `${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} ${Math.abs(Number(n || 0)) < 2 ? "pt" : "pts"}`,
  },
};
const useT = () => { const { i18n } = useTranslation(); return TXT[normLang(i18n.language)] || TXT.fr; };

// ---- status → visual (backend is the authority; frontend only maps to colors/labels) ----
function statusView(n, L) {
  const eng = n?.engagement || "NEW";
  if (eng === "NEW") return { dot: "bg-slate-400", label: L.newAccount, text: "text-slate-500" };
  const sub = eng === "ORDERS_IN_PROGRESS" ? L.ordersInProgress : eng === "ORDERED" ? L.ordered : L.deposited;
  const dot = n.completed ? "bg-emerald-500" : eng === "DEPOSITED" ? "bg-blue-500" : "bg-orange-500";
  const label = n.completed ? `${L.active} • ${sub}` : sub;
  const text = n.completed ? "text-emerald-600" : eng === "DEPOSITED" ? "text-blue-600" : "text-orange-600";
  return { dot, label, text };
}
const sideBorder = (side) => (side === "LEFT" ? "border-l-4 border-l-sky-500" : side === "RIGHT" ? "border-l-4 border-l-violet-500" : "border-l-4 border-l-primary");

// ---- non-recursive layout (iterative BFS: widths bottom-up, centres top-down) ----
function computeLayout(rootId, nodesById, childrenOf) {
  const NODE_W = 210, NODE_H = 150, SLOT = 240, LEVEL_H = 210;
  if (!rootId || !nodesById[rootId]) return { positioned: [], edges: [], width: 0, height: 0, NODE_W, NODE_H };
  const visible = [];
  const queue = [{ id: rootId, depth: 0 }];
  const seen = new Set();
  while (queue.length) {
    const cur = queue.shift();
    if (seen.has(cur.id)) continue;
    seen.add(cur.id);
    visible.push(cur);
    const c = childrenOf(cur.id);
    if (c.left) queue.push({ id: c.left, depth: cur.depth + 1 });
    if (c.right) queue.push({ id: c.right, depth: cur.depth + 1 });
  }
  const width = {};
  [...visible].sort((a, b) => b.depth - a.depth).forEach((v) => {
    const c = childrenOf(v.id);
    if (!c.left && !c.right) { width[v.id] = 1; return; }
    width[v.id] = (c.left ? (width[c.left] || 1) : 1) + (c.right ? (width[c.right] || 1) : 1);
  });
  const center = {};
  const range = { [rootId]: [0, width[rootId]] };
  const q2 = [rootId];
  while (q2.length) {
    const id = q2.shift();
    const [lo, hi] = range[id];
    center[id] = (lo + hi) / 2;
    const c = childrenOf(id);
    const lw = c.left ? (width[c.left] || 1) : 1;
    if (c.left) { range[c.left] = [lo, lo + lw]; q2.push(c.left); }
    if (c.right) { range[c.right] = [lo + lw, hi]; q2.push(c.right); }
  }
  const positioned = visible.map((v) => ({
    id: v.id, node: nodesById[v.id], depth: v.depth,
    x: center[v.id] * SLOT - NODE_W / 2, y: v.depth * LEVEL_H,
  }));
  const posById = {};
  positioned.forEach((p) => { posById[p.id] = p; });
  const edges = [];
  visible.forEach((v) => {
    const c = childrenOf(v.id);
    const parent = posById[v.id];
    [["left", c.left], ["right", c.right]].forEach(([sk, cid]) => {
      if (cid && posById[cid]) {
        const child = posById[cid];
        edges.push({ x1: parent.x + NODE_W / 2, y1: parent.y + NODE_H, x2: child.x + NODE_W / 2, y2: child.y, side: sk.toUpperCase() });
      }
    });
  });
  const w = Math.max(NODE_W, ...positioned.map((p) => p.x + NODE_W)) + 40;
  const maxDepth = Math.max(0, ...visible.map((v) => v.depth));
  return { positioned, edges, width: w, height: (maxDepth + 1) * LEVEL_H, NODE_W, NODE_H };
}

// ---- KPI card ----
function Kpi({ icon: Icon, tint, label, value, sub, testid }) {
  return (
    <div data-testid={testid} className="flex items-center gap-3 rounded-2xl border border-border bg-card p-4">
      <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${tint}`}><Icon className="h-5 w-5 text-white" /></div>
      <div className="min-w-0">
        <div className="truncate text-xs font-medium text-muted-foreground">{label}</div>
        <div className="font-display text-lg font-bold leading-tight">{value}</div>
        {sub && <div className="truncate text-[11px] text-muted-foreground">{sub}</div>}
      </div>
    </div>
  );
}

// ---- graph node card ----
function TreeNodeCard({ p, isRoot, active, onSelect, onToggle, toggleIcon, nodeW, nodeH, L }) {
  const n = p.node;
  const sv = statusView(n, L);
  return (
    <div className="absolute" style={{ left: p.x, top: p.y, width: nodeW, height: nodeH }} data-testid={`tree-node-${n.member_id}`}>
      {isRoot && <Crown className="absolute -top-4 left-1/2 z-10 h-6 w-6 -translate-x-1/2 fill-amber-400 text-amber-500" />}
      <button onClick={onSelect}
        className={`flex h-full w-full flex-col gap-1.5 rounded-2xl border bg-card p-3 text-left shadow-sm transition-all hover:shadow-md ${sideBorder(n.placement_side)} ${active ? "ring-2 ring-primary" : "border-border"}`}>
        <div className="flex items-center gap-1.5">
          <span className={`h-2 w-2 rounded-full ${sv.dot}`} />
          <span className={`truncate text-[11px] font-semibold ${sv.text}`}>{isRoot ? L.you : sv.label}</span>
        </div>
        <div className="flex items-center gap-2">
          <UserAvatar user={{ name: n.name, avatar_url: n.avatar_url }} className="h-9 w-9 shrink-0 rounded-full bg-primary/10 text-xs font-bold text-primary" />
          <div className="min-w-0">
            <div className="truncate font-mono text-xs font-bold text-foreground">{n.member_code}</div>
            <div className="truncate text-[11px] text-muted-foreground">{n.name || "—"}{n.generation != null ? ` · G${n.generation}` : ""}</div>
          </div>
        </div>
        <div className="mt-auto grid grid-cols-2 gap-1 text-[11px]">
          <span className="text-sky-600">L : {L.pts(n.left_volume)}</span>
          <span className="text-right text-violet-600">R : {L.pts(n.right_volume)}</span>
        </div>
      </button>
      {(n.child_count > 0) && (
        <button onClick={(e) => { e.stopPropagation(); onToggle(); }} data-testid={`tree-toggle-${n.member_id}`}
          className="absolute -bottom-3 left-1/2 z-10 flex h-6 w-6 -translate-x-1/2 items-center justify-center rounded-full border border-border bg-background text-foreground shadow hover:bg-secondary">
          {toggleIcon}
        </button>
      )}
    </div>
  );
}

// ---- detail panel ----
function Row({ k, v }) {
  return (<div className="flex items-center justify-between gap-3 py-1.5 text-sm"><span className="text-muted-foreground">{k}</span><span className="font-medium text-right">{v}</span></div>);
}
function DetailPanel({ detail, onClose, onViewNetwork, L }) {
  const n = detail.member;
  const sv = statusView(n, L);
  return (
    <div data-testid="tree-detail-panel" className="rounded-2xl border border-border bg-card p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="text-sm font-semibold">{L.memberDetails}</div>
        <button onClick={onClose} data-testid="tree-detail-close" className="rounded-lg p-1 text-muted-foreground hover:bg-secondary"><X className="h-4 w-4" /></button>
      </div>
      <div className="flex items-center gap-3">
        <UserAvatar user={{ name: n.name, avatar_url: n.avatar_url }} className="h-14 w-14 rounded-full bg-primary/10 text-base font-bold text-primary" />
        <div className="min-w-0">
          <div className="flex items-center gap-1.5"><span className={`h-2 w-2 rounded-full ${sv.dot}`} /><span className={`text-xs font-semibold ${sv.text}`}>{sv.label}</span></div>
          <div className="truncate font-mono text-sm font-bold">{n.member_code}</div>
          <div className="truncate text-xs text-muted-foreground">{n.name || "—"}</div>
        </div>
      </div>

      <div className="mt-3 border-t border-border pt-2">
        <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{L.info}</div>
        <Row k={L.directSponsor} v={detail.sponsor_code || "—"} />
        <Row k={L.placement} v={detail.is_self ? L.rootYou : (n.placement_side === "LEFT" ? L.leftW : n.placement_side === "RIGHT" ? L.rightW : "—")} />
        <Row k={L.generation} v={n.generation != null ? `G${n.generation}` : "—"} />
      </div>

      <div className="mt-2 border-t border-border pt-2">
        <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{L.volumes}</div>
        <Row k={L.leftVolume} v={L.pts(n.left_volume)} />
        <Row k={L.rightVolume} v={L.pts(n.right_volume)} />
        <Row k={L.leftCarry} v={L.pts(n.left_carry)} />
        <Row k={L.rightCarry} v={L.pts(n.right_carry)} />
        <Row k={L.matching} v={L.pts(n.matched_volume)} />
      </div>

      {detail.bonus && (
        <div className="mt-2 border-t border-border pt-2" data-testid="tree-detail-bonus">
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{L.binaryBonus}</div>
          <Row k={L.available} v={money(detail.bonus.available)} />
          <Row k={L.pending} v={money(detail.bonus.pending)} />
          <Row k={L.totalGenerated} v={money(detail.bonus.total_generated)} />
        </div>
      )}

      <div className="mt-2 border-t border-border pt-2">
        <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{L.network}</div>
        <Row k={L.descendants} v={`${detail.network.descendants} ${L.membersWord}`} />
        <Row k={L.leftMembers} v={`${detail.network.left_count}`} />
        <Row k={L.rightMembers} v={`${detail.network.right_count}`} />
      </div>

      {detail.can_view_network && (
        <button onClick={() => onViewNetwork(n)} data-testid="tree-view-network-btn"
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:opacity-90">
          <Network className="h-4 w-4" /> {L.viewNetwork}
        </button>
      )}
    </div>
  );
}

export default function PartnerBinaryTree() {
  const L = useT();
  const [overview, setOverview] = useState(null);
  const [depth, setDepth] = useState(3);
  const [treeData, setTreeData] = useState(null);
  const [crumbs, setCrumbs] = useState([]); // [{member_id, member_code}]
  const [collapsed, setCollapsed] = useState({}); // member_id -> true
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  const currentRoot = crumbs[crumbs.length - 1]?.member_id || null;

  const loadOverview = useCallback(async () => {
    try {
      const { data } = await api.get("/partner/binary/overview");
      setOverview(data);
      setCrumbs([{ member_id: data.me.member_id, member_code: data.me.member_code }]);
    } catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
  }, []);

  const loadTree = useCallback(async (rootId, d) => {
    setLoading(true);
    try {
      const { data } = await api.get("/partner/binary/tree", { params: { root: rootId, depth: d } });
      setTreeData(data);
    } catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadOverview(); }, [loadOverview]);
  useEffect(() => { if (currentRoot) loadTree(currentRoot, depth); }, [currentRoot, depth, loadTree]);

  const nodesById = useMemo(() => {
    const map = {};
    (treeData?.nodes || []).forEach((n) => { map[n.member_id] = n; });
    return map;
  }, [treeData]);

  const childrenOf = useCallback((id) => {
    const n = nodesById[id];
    if (!n || collapsed[id]) return { left: null, right: null };
    const left = n.left_child_id && nodesById[n.left_child_id] ? n.left_child_id : null;
    const right = n.right_child_id && nodesById[n.right_child_id] ? n.right_child_id : null;
    return { left, right };
  }, [nodesById, collapsed]);

  const layout = useMemo(() => computeLayout(currentRoot, nodesById, childrenOf), [currentRoot, nodesById, childrenOf]);

  const select = useCallback(async (id) => {
    setSelected(id);
    try {
      const { data } = await api.get(`/partner/binary/member/${id}`);
      setDetail(data);
    } catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
  }, []);

  const onToggle = (n) => {
    const id = n.member_id;
    const hasLoadedChildren = (n.left_child_id && nodesById[n.left_child_id]) || (n.right_child_id && nodesById[n.right_child_id]);
    if (hasLoadedChildren) setCollapsed((p) => ({ ...p, [id]: !p[id] }));
    else viewNetwork(n);
  };

  const viewNetwork = (n) => {
    setCrumbs((prev) => {
      const idx = prev.findIndex((c) => c.member_id === n.member_id);
      if (idx >= 0) return prev.slice(0, idx + 1);
      return [...prev, { member_id: n.member_id, member_code: n.member_code }];
    });
    setCollapsed({});
  };

  const goCrumb = (idx) => { setCrumbs((prev) => prev.slice(0, idx + 1)); setCollapsed({}); };
  const resetRoot = () => { if (overview) { setCrumbs([{ member_id: overview.me.member_id, member_code: overview.me.member_code }]); setCollapsed({}); } };

  const k = overview?.kpi;

  return (
    <div className="space-y-5" data-testid="partner-binary-tree-page">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">{L.pageTitle}</h1>
        <p className="text-sm text-muted-foreground">{L.pageSub}</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Kpi testid="kpi-left" icon={Users} tint="bg-sky-500" label={L.myLeftBranch} value={L.pts(k?.left_volume)} sub={`${L.carry} : ${L.pts(k?.left_carry)}`} />
        <Kpi testid="kpi-right" icon={Network} tint="bg-violet-500" label={L.myRightBranch} value={L.pts(k?.right_volume)} sub={`${L.carry} : ${L.pts(k?.right_carry)}`} />
        <Kpi testid="kpi-matching" icon={Scale} tint="bg-emerald-500" label={L.currentMatching} value={L.pts(k?.matching)} />
        <Kpi testid="kpi-bonus" icon={Gift} tint="bg-amber-500" label={L.binaryBonus} value={money(k?.bonus_available)} sub={L.available} />
        <Kpi testid="kpi-descendants" icon={Users} tint="bg-slate-600" label={L.myDescendants} value={`${k?.descendants ?? 0}`} sub={L.totalMembers} />
      </div>

      {/* breadcrumb + level control */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-card px-4 py-3">
        <div className="flex flex-wrap items-center gap-1 text-sm" data-testid="tree-breadcrumb">
          <button onClick={resetRoot} data-testid="tree-crumb-home" className="flex items-center gap-1 font-medium text-primary hover:underline"><Home className="h-4 w-4" /> {L.myTree}</button>
          {crumbs.slice(1).map((c, i) => (
            <span key={c.member_id} className="flex items-center gap-1">
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
              <button onClick={() => goCrumb(i + 1)} data-testid={`tree-crumb-${c.member_id}`} className="font-mono text-xs font-semibold hover:underline">{c.member_code}</button>
            </span>
          ))}
        </div>
        <div className="flex items-center gap-2">
          {crumbs.length > 1 && <button onClick={resetRoot} data-testid="tree-back-home" className="rounded-lg border border-border px-3 py-1.5 text-xs font-medium hover:bg-secondary">{L.backToTree}</button>}
          <span className="flex items-center gap-1 text-xs text-muted-foreground"><Layers className="h-3.5 w-3.5" /> {L.levels} :</span>
          {[1, 2, 3, 4, 5].map((d) => (
            <button key={d} onClick={() => setDepth(d)} data-testid={`tree-depth-${d}`}
              className={`h-7 w-7 rounded-lg text-xs font-semibold transition-colors ${depth === d ? "bg-primary text-primary-foreground" : "border border-border hover:bg-secondary"}`}>{d}</button>
          ))}
        </div>
      </div>

      {/* main: graph + detail */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_340px]">
        <div className="relative overflow-auto rounded-2xl border border-border bg-gradient-to-b from-secondary/30 to-background p-4" style={{ minHeight: 420, maxHeight: 680 }} data-testid="tree-canvas">
          {loading ? (
            <div className="flex h-full min-h-[380px] items-center justify-center text-sm text-muted-foreground">{L.loadingNetwork}</div>
          ) : (
            <div className="relative" style={{ width: layout.width, height: layout.height, minWidth: "100%" }}>
              <svg className="pointer-events-none absolute inset-0" width={layout.width} height={layout.height}>
                {layout.edges.map((e, i) => (
                  <path key={i} d={`M ${e.x1} ${e.y1} C ${e.x1} ${(e.y1 + e.y2) / 2}, ${e.x2} ${(e.y1 + e.y2) / 2}, ${e.x2} ${e.y2}`}
                    stroke={e.side === "LEFT" ? "#0ea5e9" : "#8b5cf6"} strokeWidth="2" fill="none" opacity="0.55" />
                ))}
              </svg>
              {layout.positioned.map((p) => (
                <TreeNodeCard key={p.id} p={p} isRoot={p.id === currentRoot && crumbs.length === 1} active={selected === p.id}
                  onSelect={() => select(p.id)} onToggle={() => onToggle(p.node)} L={L}
                  toggleIcon={collapsed[p.id] || !((p.node.left_child_id && nodesById[p.node.left_child_id]) || (p.node.right_child_id && nodesById[p.node.right_child_id])) ? <Plus className="h-3.5 w-3.5" /> : <Minus className="h-3.5 w-3.5" />}
                  nodeW={layout.NODE_W} nodeH={layout.NODE_H} />
              ))}
            </div>
          )}
        </div>

        <div>
          {detail ? (
            <DetailPanel detail={detail} onClose={() => { setDetail(null); setSelected(null); }} onViewNetwork={viewNetwork} L={L} />
          ) : (
            <div className="rounded-2xl border border-dashed border-border bg-card p-6 text-center text-sm text-muted-foreground" data-testid="tree-detail-empty">
              {L.clickMember}
            </div>
          )}
          <div className="mt-3 flex items-start gap-2 rounded-2xl border border-border bg-blue-500/5 p-3 text-xs text-muted-foreground" data-testid="tree-upline-note">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" />
            {L.uplineNote}
          </div>
        </div>
      </div>

      {/* legend + how it works */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-2xl border border-border bg-card p-4" data-testid="tree-legend">
          <div className="mb-2 text-sm font-semibold">{L.statusLegend}</div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-slate-400" /><div><div className="font-medium">{L.newAccount}</div><div className="text-muted-foreground">{L.legNewDesc}</div></div></div>
            <div className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-blue-500" /><div><div className="font-medium">{L.deposited}</div><div className="text-muted-foreground">{L.legDepDesc}</div></div></div>
            <div className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-orange-500" /><div><div className="font-medium">{L.ordered}</div><div className="text-muted-foreground">{L.legOrdDesc}</div></div></div>
            <div className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500" /><div><div className="font-medium">{L.active}</div><div className="text-muted-foreground">{L.legActiveDesc}</div></div></div>
          </div>
          <div className="mt-3 flex items-center gap-4 border-t border-border pt-2 text-xs">
            <span className="flex items-center gap-1"><ArrowDownLeft className="h-4 w-4 text-sky-500" /> {L.leftBranch}</span>
            <span className="flex items-center gap-1"><ArrowDownRight className="h-4 w-4 text-violet-500" /> {L.rightBranch}</span>
          </div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-4" data-testid="tree-howitworks">
          <div className="mb-2 text-sm font-semibold">{L.howItWorks}</div>
          <ol className="space-y-1.5 text-xs text-muted-foreground">
            <li>{L.hiw1}</li>
            <li>{L.hiw2}</li>
            <li>{L.hiw3}</li>
            <li>{L.hiw4}</li>
          </ol>
        </div>
      </div>
    </div>
  );
}
